import { Link } from 'wouter';
import { Logo } from '@/components/ui/Logo';
import { Watermark } from '@/components/brand/Watermark';
import { site } from '@/content/site';
import { services } from '@/content/services';
import { SocialLinks } from '@/components/ui/SocialLinks';
import { useLogoClick } from '@/lib/useScrollTop';

export function Footer() {
  const year = new Date().getFullYear();
  const onLogoClick = useLogoClick();

  return (
    <footer className="relative overflow-hidden bg-ink text-white">
      {/* Client-directed: watermark bleeds off the bottom-left corner. */}
      <Watermark position="bottom-left" scale={1.6} opacity={0.04} />

      <div className="shell relative z-10 py-section-sm md:py-section-md">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Link
              href="/"
              onClick={onLogoClick}
              className="logo-hover inline-block text-white"
              aria-label={`${site.legalName} — home`}
            >
              <Logo variant="lockup" size={36} title="" />
            </Link>
            <p className="mt-6 max-w-[34ch] text-sm leading-relaxed text-white/60">
              Software, AI and automation built by a team that ships — from first line of code to
              ongoing support.
            </p>
          </div>

          <div>
            <h2 className="text-eyebrow mb-5 text-white/65">Services</h2>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    href={`/services/${s.slug}`}
                    className="inline-block py-1.5 text-sm text-white/70 transition-colors hover:text-white"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-eyebrow mb-5 text-white/65">Company</h2>
            <ul className="space-y-3">
              {site.nav.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="inline-block py-1.5 text-sm text-white/70 transition-colors hover:text-white"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              {/*
                Not part of site.nav: the policy belongs in the footer, but
                putting it in that array would also put it in the header nav.
              */}
              <li>
                <Link
                  href="/privacy"
                  className="inline-block py-1.5 text-sm text-white/70 transition-colors hover:text-white"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-eyebrow mb-5 text-white/65">Get in touch</h2>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${site.email.general}`}
                  className="inline-block py-1.5 text-sm text-white/70 transition-colors hover:text-white"
                >
                  {site.email.general}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${site.email.careers}`}
                  className="inline-block py-1.5 text-sm text-white/70 transition-colors hover:text-white"
                >
                  {site.email.careers}
                </a>
              </li>
            </ul>

            <h2 className="text-eyebrow mt-8 mb-5 text-white/65">Locations</h2>
            <ul className="space-y-3">
              {site.locations.map((loc) => (
                <li key={loc.city} className="text-sm text-white/70">
                  {loc.city}, {loc.country}
                  <span className="ml-2 text-white/65">{loc.timezone}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <SocialLinks onDark />
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-sm text-white/45 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} {site.legalName}. All rights reserved.
          </p>
          {/* Curly quotes, not straight ones — this is display text, not code. */}
          <p className="italic">“{site.tagline}”</p>
        </div>
      </div>

      <div className="footer-wordmark" aria-hidden="true">FALCON</div>
    </footer>
  );
}
